/**
 * importApi — AK-368c клиент импорта полей из файла (KML/KMZ/SHP-zip/GeoJSON).
 *
 * Два боевых пути (контракт бэка — PR #410 «AK-368b»):
 *   1. `POST /api/v1/fields/import/preview` (multipart, поле `file`) —
 *      НИЧЕГО не создаёт. Парсит файл, валидирует фичи, считает площадь.
 *      Ответ: { features: [...], warnings: [...] }.
 *   2. `POST /api/v1/fields/import/save` (JSON) — батч-сохранение выбранных
 *      valid-фич: farm_id + список {geometry, name?, number?, area_ha?}.
 *      Ответ: { parsed, created, updated, skipped, errors[] }.
 *
 * Лимит превью — 500 фич (сверх → усечение + warning от бэка). Проекция не в
 * градусах / нет геометрии → feature-level valid:false + error (НЕ 500). Ошибки
 * файла целиком: 400 (битый/непонятный), 413 (>50 МБ), 401/403 (auth/guard).
 *
 * Чистый парсер `parsePreviewResponse` экспортируется для юнит-теста (нормализует
 * потенциально кривой ответ в предсказуемую форму, не роняя UI).
 */
import { api, ApiError } from '@/lib/api';

/** GeoJSON MultiPolygon / Polygon внешних контуров (WGS84). */
export interface ImportGeometry {
  type: 'MultiPolygon' | 'Polygon';
  coordinates: number[][][] | number[][][][];
}

/**
 * Дубликат: существующее поле, с которым импорт-контур пересекается ≥ порога.
 * AK-369 — бэк отдаёт это в `preview.features[].duplicates` (PR feat/ak-369-
 * batch-rollback). Если бэк не отдал поле (старый ответ AK-368) — массив пустой,
 * и UI дедуп-группа просто не появляется. Порог/дубликаты считает бэк (PostGIS
 * ST_Area(ST_Intersection)/ST_Area) — точнее клиентского best-effort.
 */
export interface DuplicateMatch {
  /** UUID существующего поля-дубликата. */
  field_id: string;
  /** Имя существующего поля (для бейджа «⚠ дубликат: <имя>»). */
  name: string;
  /** Процент перекрытия импорт-контура существующим полем (0..100). */
  overlap_pct: number;
}

/** Одна фича из превью-ответа бэка (нормализованная). */
export interface PreviewFeature {
  /** 1-based стабильный ключ фичи (из бэка). Для React-key и трекинга правок. */
  index: number;
  /** WGS84-геометрия; null если фича невалидна и геометрию не привести. */
  geometry: ImportGeometry | null;
  /** Имя из файла (kml_name/props); null если нет — UI предложит автоген. */
  suggested_name: string | null;
  /** Номер из файла; null если нет. */
  number: string | null;
  /** Площадь га (ST_Area geography); null если не посчитать. */
  area_ha: number | null;
  /** Можно ли сохранять эту фичу. */
  valid: boolean;
  /** Причина valid=false ИЛИ warning об авто-починке геометрии (make_valid). */
  error: string | null;
  /**
   * Дубликаты — существующие поля, с которыми контур пересекается ≥ порога.
   * AK-369. Пусто, если пересечений нет ИЛИ бэк ещё не отдаёт (fallback).
   */
  duplicates: DuplicateMatch[];
}

/** Нормализованный результат превью. */
export interface PreviewResult {
  features: PreviewFeature[];
  warnings: string[];
}

/** Тело `/import/save`. */
export interface SaveFeaturePayload {
  geometry: ImportGeometry;
  name?: string;
  number?: string | null;
  area_ha?: number | null;
}

export interface SavePayload {
  farm_id: string;
  brigade_id?: string | null;
  features: SaveFeaturePayload[];
}

/**
 * Ответ `/import/save` (ImportResult). AK-369 добавляет `batch_id` — идентификатор
 * пачки для отката (`POST /import/{batch_id}/rollback`). Бэк отдаёт его как
 * `import_batch_id` (BaseModel ImportResult); нормализуем в `batch_id` для UI.
 * Старый бэк (AK-368) поля не отдаёт → null, и кнопка отката не появляется.
 */
export interface SaveResult {
  parsed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  /** AK-369: id пачки для отката (из бэкового `import_batch_id`). null если нет. */
  batch_id: string | null;
}

/**
 * AK-369: одна пачка импорта в «Последних импортах» (GET /import/batches).
 * Бэк-контракт (ImportBatchRow): `import_batch_id`, `created_at`,
 * `field_count` (только АКТИВНЫЕ поля пачки), `created_by_name`. Уже откатанная
 * пачка (все поля архивны) в ответе НЕ приходит — выпадает из списка. Поэтому
 * `rolled_back` — чисто клиентский оптимистичный флаг: строка, которую мы сами
 * откатили в этой сессии, остаётся line-through до перезагрузки списка.
 */
export interface ImportBatch {
  /** id пачки (из бэкового `import_batch_id`). */
  batch_id: string;
  /** ISO-дата создания (MIN created_at полей пачки). */
  created_at: string;
  /** Сколько АКТИВНЫХ полей в пачке. */
  count: number;
  /** Имя импортёра (created_by_name), либо null. */
  author: string | null;
  /** Клиентский оптимистичный флаг: откатана в текущей сессии → line-through. */
  rolled_back?: boolean;
}

/** AK-369: ответ отката пачки (ImportRollbackResult). */
export interface RollbackResult {
  /** id откатанной пачки. */
  batch_id: string;
  /** Сколько полей архивировано этим вызовом (0 если уже была откатана). */
  archived: number;
}

/** AK-369: ответ восстановления пачки (обратная операция к rollback). */
export interface RestoreResult {
  /** id восстановленной пачки. */
  batch_id: string;
  /** Сколько полей разархивировано этим вызовом (0 если уже активна). */
  restored: number;
}

/**
 * Тип ошибки препроцессинга файла целиком (НЕ per-feature). UI показывает
 * центрированную карточку из фрейма 6 в зависимости от kind.
 */
export type ImportFileErrorKind =
  | 'unreadable' // 400 — битый архив / не тот формат / нет .kml в KMZ
  | 'too-large' // 413 — файл > 50 МБ
  | 'forbidden' // 403 — нет прав (guard)
  | 'unauthorized' // 401
  | 'network' // сеть/оффлайн/непонятный сбой
  | 'empty'; // прочитан, но 0 валидных контуров (решается на UI по features)

export class ImportFileError extends Error {
  constructor(
    public kind: ImportFileErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'ImportFileError';
  }
}

/** Расширения, которые принимаем в дропзоне. */
export const ACCEPTED_EXTENSIONS = ['.kml', '.kmz', '.zip', '.geojson', '.json'];
/** Максимум контуров за один импорт (зеркалит лимит бэка). */
export const MAX_IMPORT_FEATURES = 500;

/**
 * Нормализует потенциально кривой ответ превью в предсказуемую форму.
 * Не бросает — незнакомые/битые поля становятся безопасными дефолтами
 * (geometry:null, valid:false), чтобы UI не падал на неожиданном payload'е.
 *
 * Экспортируется для юнит-теста.
 */
export function parsePreviewResponse(body: unknown): PreviewResult {
  if (!body || typeof body !== 'object') return { features: [], warnings: [] };
  const b = body as { features?: unknown; warnings?: unknown };
  const rawFeatures = Array.isArray(b.features) ? b.features : [];
  const features: PreviewFeature[] = rawFeatures.map((raw, i) =>
    normalizeFeature(raw, i + 1),
  );
  const warnings = Array.isArray(b.warnings)
    ? b.warnings.filter((w): w is string => typeof w === 'string')
    : [];
  return { features, warnings };
}

function normalizeFeature(raw: unknown, fallbackIndex: number): PreviewFeature {
  const f = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const index = typeof f.index === 'number' && Number.isFinite(f.index) ? f.index : fallbackIndex;
  const geometry = normalizeGeometry(f.geometry);
  const suggested_name =
    typeof f.suggested_name === 'string' && f.suggested_name.trim()
      ? f.suggested_name
      : null;
  const number =
    typeof f.number === 'string' && f.number.trim()
      ? f.number
      : typeof f.number === 'number'
        ? String(f.number)
        : null;
  const area_ha =
    typeof f.area_ha === 'number' && Number.isFinite(f.area_ha) ? f.area_ha : null;
  // valid=true только если бэк явно сказал true И геометрия есть.
  const valid = f.valid === true && geometry !== null;
  const error = typeof f.error === 'string' && f.error.trim() ? f.error : null;
  const duplicates = normalizeDuplicates(f.duplicates);
  return { index, geometry, suggested_name, number, area_ha, valid, error, duplicates };
}

/**
 * Нормализует `duplicates` из превью-фичи. Незнакомый/битый payload → [] (UI не
 * падает, дедуп-группа не появляется — эквивалент «бэк ещё не отдаёт», AK-369).
 */
function normalizeDuplicates(raw: unknown): DuplicateMatch[] {
  if (!Array.isArray(raw)) return [];
  const out: DuplicateMatch[] = [];
  for (const d of raw) {
    if (!d || typeof d !== 'object') continue;
    const o = d as Record<string, unknown>;
    const field_id =
      typeof o.field_id === 'string' && o.field_id.trim() ? o.field_id : null;
    if (!field_id) continue;
    const name =
      typeof o.name === 'string' && o.name.trim() ? o.name : 'существующее поле';
    const overlap_pct =
      typeof o.overlap_pct === 'number' && Number.isFinite(o.overlap_pct)
        ? Math.max(0, Math.min(100, Math.round(o.overlap_pct)))
        : 0;
    out.push({ field_id, name, overlap_pct });
  }
  // По убыванию перекрытия — самый «жирный» дубль первым в бейдже.
  out.sort((a, b) => b.overlap_pct - a.overlap_pct);
  return out;
}

function normalizeGeometry(raw: unknown): ImportGeometry | null {
  if (!raw || typeof raw !== 'object') return null;
  const g = raw as { type?: unknown; coordinates?: unknown };
  if ((g.type === 'MultiPolygon' || g.type === 'Polygon') && Array.isArray(g.coordinates)) {
    return { type: g.type, coordinates: g.coordinates as ImportGeometry['coordinates'] };
  }
  return null;
}

/**
 * Отправить файл на превью-парсинг. Возвращает нормализованный результат.
 *
 * `farmId` — хозяйство из переключателя (ADR-081 rev.6 D5): админу хозяйства
 * превью открыто только «по хозяйству» — без `farm_id` бэк отвечает 403, а
 * дубли считает по читаемым хозяйствам. Для писателей компании поле ничего не
 * меняет. Хозяйство не выбрано — поле не шлём (путь компании, как раньше).
 *
 * @throws ImportFileError при ошибке файла целиком (не per-feature).
 */
export async function previewImport(file: File, farmId?: string | null): Promise<PreviewResult> {
  const fd = new FormData();
  fd.append('file', file);
  if (farmId) fd.append('farm_id', farmId);
  let body: unknown;
  try {
    body = await api.uploadFile<unknown>('/api/v1/fields/import/preview', fd);
  } catch (err) {
    throw toFileError(err);
  }
  return parsePreviewResponse(body);
}

/** Сохранить выбранные фичи пачкой. Нормализует batch_id (AK-369). */
export async function saveImport(payload: SavePayload): Promise<SaveResult> {
  try {
    const raw = await api.post<Record<string, unknown>>(
      '/api/v1/fields/import/save',
      payload,
    );
    const num = (v: unknown): number =>
      typeof v === 'number' && Number.isFinite(v) ? v : 0;
    return {
      parsed: num(raw.parsed),
      created: num(raw.created),
      updated: num(raw.updated),
      skipped: num(raw.skipped),
      errors: Array.isArray(raw.errors)
        ? raw.errors.filter((e): e is string => typeof e === 'string')
        : [],
      // AK-369: id пачки для отката. Бэк отдаёт как `import_batch_id`
      // (ImportResult). Старый бэк (AK-368) поля не отдаёт → null.
      batch_id:
        typeof raw.import_batch_id === 'string' && raw.import_batch_id.trim()
          ? raw.import_batch_id
          : null,
    };
  } catch (err) {
    throw toFileError(err);
  }
}

/**
 * AK-369: список последних пачек импорта (для «Последних импортов» + отката).
 * Бэк-контракт (ImportBatchRow): `import_batch_id`, `created_at`, `field_count`,
 * `created_by_name`. Уже откатанная пачка не приходит (все поля архивны). Если
 * эндпоинта ещё нет (404/бэк не готов) — возвращаем [] и раздел «Последние
 * импорты» просто не рисуется (graceful degradation).
 */
export async function listImportBatches(): Promise<ImportBatch[]> {
  let raw: unknown;
  try {
    raw = await api.get<unknown>('/api/v1/fields/import/batches');
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const out: ImportBatch[] = [];
  for (const b of raw) {
    if (!b || typeof b !== 'object') continue;
    const o = b as Record<string, unknown>;
    const batch_id =
      typeof o.import_batch_id === 'string' && o.import_batch_id.trim()
        ? o.import_batch_id
        : null;
    if (!batch_id) continue;
    out.push({
      batch_id,
      created_at: typeof o.created_at === 'string' ? o.created_at : '',
      count:
        typeof o.field_count === 'number' && Number.isFinite(o.field_count)
          ? o.field_count
          : 0,
      author:
        typeof o.created_by_name === 'string' && o.created_by_name.trim()
          ? o.created_by_name
          : null,
      rolled_back: false,
    });
  }
  return out;
}

/**
 * AK-369: откатить (архивировать) пачку импорта. Идемпотентно и обратимо — бэк
 * архивирует поля пачки (как AK-340). Контракт: `POST /api/v1/fields/import/
 * {batch_id}/rollback` → {import_batch_id, archived}.
 */
export async function rollbackImportBatch(batchId: string): Promise<RollbackResult> {
  try {
    const raw = await api.post<Record<string, unknown>>(
      `/api/v1/fields/import/${batchId}/rollback`,
      {},
    );
    return {
      batch_id:
        typeof raw.import_batch_id === 'string' && raw.import_batch_id.trim()
          ? raw.import_batch_id
          : batchId,
      archived:
        typeof raw.archived === 'number' && Number.isFinite(raw.archived)
          ? raw.archived
          : 0,
    };
  } catch (err) {
    throw toFileError(err);
  }
}

/**
 * AK-369: восстановить (разархивировать) откатанную пачку импорта — обратная
 * операция к rollback. Контракт: `POST /api/v1/fields/import/{batch_id}/restore`
 * → {import_batch_id, restored}. Используется кнопкой «Восстановить» на строке
 * с line-through в «Последних импортах» (в рамках сессии, пока строка видна).
 */
export async function restoreImportBatch(batchId: string): Promise<RestoreResult> {
  try {
    const raw = await api.post<Record<string, unknown>>(
      `/api/v1/fields/import/${batchId}/restore`,
      {},
    );
    return {
      batch_id:
        typeof raw.import_batch_id === 'string' && raw.import_batch_id.trim()
          ? raw.import_batch_id
          : batchId,
      restored:
        typeof raw.restored === 'number' && Number.isFinite(raw.restored)
          ? raw.restored
          : 0,
    };
  } catch (err) {
    throw toFileError(err);
  }
}

/** Маппинг ApiError/сетевого сбоя в типизированную ImportFileError для UI. */
export function toFileError(err: unknown): ImportFileError {
  if (err instanceof ApiError) {
    if (err.status === 413) {
      return new ImportFileError('too-large', 'Файл слишком большой (максимум 50 МБ).');
    }
    if (err.status === 403) {
      return new ImportFileError(
        'forbidden',
        'Импорт полей доступен только администратору компании.',
      );
    }
    if (err.status === 401) {
      return new ImportFileError('unauthorized', 'Сессия истекла. Войдите заново.');
    }
    // 400 и всё прочее от бэка = файл не удалось прочитать/распарсить.
    return new ImportFileError(
      'unreadable',
      err.detail ||
        'Файл повреждён или это не KML / KMZ / SHP. Проверьте, что SHP лежит в zip вместе с .dbf и .shx.',
    );
  }
  return new ImportFileError(
    'network',
    'Не удалось загрузить файл. Проверьте соединение и попробуйте ещё раз.',
  );
}

// ─── AK-369: отделения/бригады для формы сохранения ────────────────────────
//
// Импорт назначает пачку на хозяйство (обязательно) + опционально на
// отделение/бригаду. Бэк: GET /brigades?farm_id=… (список), POST /brigades
// (создать инлайн). Оба — существующий brigades-роутер (fields-crops домен).

/** Отделение/бригада хозяйства (для дропдауна назначения). */
export interface Brigade {
  id: string;
  farm_id: string;
  name: string;
}

/**
 * Список отделений/бригад хозяйства. Пусто (или ошибка) → в форме поле просто
 * не показывается (структуры нет — поле привязывается к хозяйству, wireframe).
 */
export async function listBrigades(farmId: string): Promise<Brigade[]> {
  let raw: unknown;
  try {
    raw = await api.get<unknown>(
      `/api/v1/brigades?farm_id=${encodeURIComponent(farmId)}`,
    );
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const out: Brigade[] = [];
  for (const b of raw) {
    if (!b || typeof b !== 'object') continue;
    const o = b as Record<string, unknown>;
    if (typeof o.id === 'string' && typeof o.name === 'string') {
      out.push({
        id: o.id,
        farm_id: typeof o.farm_id === 'string' ? o.farm_id : farmId,
        name: o.name,
      });
    }
  }
  return out;
}

/**
 * Создать отделение/бригаду инлайн из формы сохранения (без ухода на страницу
 * структуры, wireframe фрейм 5). Бросает ApiError при ошибке — вызывающий
 * показывает сообщение и не переключает выбор.
 */
export async function createBrigade(farmId: string, name: string): Promise<Brigade> {
  const raw = await api.post<Record<string, unknown>>('/api/v1/brigades', {
    farm_id: farmId,
    name,
  });
  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    farm_id: typeof raw.farm_id === 'string' ? raw.farm_id : farmId,
    name: typeof raw.name === 'string' ? raw.name : name,
  };
}
